import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { PageHeader, Panel, StatusBadge, Table, T, EmptyState, Skeleton, money, shortDate, DeleteButton, MobileCards } from "@/components/admin/ui";

const STATUS_FILTERS = [
  ["", "Tous"], ["draft", "Brouillons"], ["sent", "Envoyés"], ["accepted", "Acceptés"],
  ["converted", "Convertis"], ["rejected", "Refusés"], ["expired", "Expirés"],
];

export default function AdminQuotes() {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const navigate = useNavigate();

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status) params.set("status", status);
    adminApi.get(`/quotes?${params}`).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  useEffect(() => { adminApi.get("/billing/summary").then((r) => setSummary(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="admin-quotes">
      <PageHeader title="Devis" subtitle="Propositions commerciales — acceptation en ligne par le client"
        actions={<Link to="/admin/app/quotes/new" data-testid="quote-new-link" className={T.btnPrimary}>
          <Plus className="h-4 w-4" strokeWidth={2} /> Nouveau devis
        </Link>} />

      {summary && (summary.quotes_open > 0 || summary.quotes_accepted > 0) && (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="quotes-kpis">
          <div className="rounded-2xl border border-ink/[0.07] bg-white p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate/60">En attente de réponse</p>
            <p className="mt-1 font-mono text-xl font-semibold text-ink">{summary.quotes_open}</p>
          </div>
          <div className="rounded-2xl border border-ink/[0.07] bg-white p-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate/60">Acceptés / convertis</p>
            <p className="mt-1 font-mono text-xl font-semibold text-emerald-600">{summary.quotes_accepted}</p>
          </div>
        </div>
      )}

      <Panel padded={false}
        title={<span className="relative block w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate/50" strokeWidth={2} />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} onBlur={load}
            placeholder="N°, client…" data-testid="quotes-search"
            className="h-10 w-full rounded-xl border border-ink/10 bg-cloud pl-9 pr-3 text-sm text-ink outline-none transition-colors placeholder:text-slate/60 focus:border-miles" />
        </span>}>
        <div className="flex gap-1.5 overflow-auto px-5 pt-4">
          {STATUS_FILTERS.map(([value, label]) => (
            <button key={value} onClick={() => setStatus(value)} data-testid={`filter-${value || "all"}`}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                status === value ? "bg-miles text-white" : "text-slate hover:bg-ink/[0.04]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="mt-2">
          {!data ? <Skeleton rows={6} /> : data.quotes.length === 0 ? (
            <EmptyState title="Aucun devis" hint="Créez une proposition commerciale — le client l'accepte en ligne."
              action={<Link to="/admin/app/quotes/new" className={T.btnSecondary}>Créer un devis</Link>} testId="quotes-empty" />
          ) : (
            <>
            <div className="hidden md:block">
            <Table head={["N°", "Client", "Créé le", "Valable jusqu'au", "Montant", "Version", "Statut", ""]}>
              {data.quotes.map((quote) => (
                <tr key={quote.id} className="cursor-pointer transition-colors hover:bg-cloud/60"
                  onClick={() => navigate(`/admin/app/quotes/${quote.id}`)} data-testid={`quote-row-${quote.number}`}>
                  <td className="font-mono text-[13px] font-semibold text-ink">{quote.number}</td>
                  <td>
                    <p className="font-medium text-ink">{quote.customer?.name}</p>
                    <p className="text-xs text-slate">{quote.service_info?.route || quote.customer?.email}</p>
                  </td>
                  <td className="text-slate">{shortDate(quote.created_at)}</td>
                  <td className={quote.status === "expired" ? "font-medium text-red-600" : "text-slate"}>{shortDate(quote.valid_until)}</td>
                  <td className="font-mono text-[13px] font-semibold text-ink">{money(quote.total)}</td>
                  <td className="font-mono text-xs text-slate">v{quote.version || 1}</td>
                  <td><StatusBadge status={quote.status} /></td>
                  <td className="text-right" onClick={(e) => e.stopPropagation()}>
                    <DeleteButton small testId={`quote-delete-${quote.number}`}
                      onConfirm={() => adminApi.delete(`/quotes/${quote.id}`)}
                      onDeleted={() => setData((d) => ({ ...d, quotes: d.quotes.filter((x) => x.id !== quote.id), total: d.total - 1 }))} />
                  </td>
                </tr>
              ))}
            </Table>
            </div>
            <MobileCards testId="quotes-cards-mobile">
              {data.quotes.map((quote) => (
                <div key={quote.id} onClick={() => navigate(`/admin/app/quotes/${quote.id}`)}
                  data-testid={`quote-card-${quote.number}`}
                  className="rounded-2xl border border-ink/[0.07] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-transform active:scale-[0.99]">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[13px] font-semibold text-ink">{quote.number} <span className="text-slate">· v{quote.version || 1}</span></span>
                    <StatusBadge status={quote.status} />
                  </div>
                  <p className="mt-1.5 font-medium text-ink">{quote.customer?.name}</p>
                  {(quote.service_info?.route || quote.customer?.email) && (
                    <p className="truncate text-xs text-slate">{quote.service_info?.route || quote.customer?.email}</p>
                  )}
                  <div className="mt-3 flex items-end justify-between">
                    <div className="text-[11px] text-slate">
                      <p>Créé {shortDate(quote.created_at)}</p>
                      <p className={quote.status === "expired" ? "font-semibold text-red-600" : ""}>Valable jusqu'au {shortDate(quote.valid_until)}</p>
                    </div>
                    <p className="font-mono text-[15px] font-semibold text-ink">{money(quote.total)}</p>
                  </div>
                </div>
              ))}
            </MobileCards>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}
